<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

const mobViewRef = ref(null)
const mobItemsContainerRef = ref(null)
const firstDeskItemRef = ref(null)
const mobItemsRefs = ref([])
const desktopMobItemHeights = ref([])
const activeTransformIndex = ref(-1)
const deskItemsRefs = ref([])
const desktopDeskItemPositions = ref([])
let resizeObserver = null

const shouldHideHeadElements = computed(() => {
  const itemsCount = props.block?.['items']?.length ?? 0
  if (!itemsCount) return false
  return activeTransformIndex.value > 0
})

const setFirstDeskItemRef = (el) => {
  firstDeskItemRef.value = el || null
}

const setMobItemRef = (idx) => (el) => {
  mobItemsRefs.value[idx] = el || null
}

const setDeskItemRef = (idx) => (el) => {
  deskItemsRefs.value[idx] = el || null
}

const syncMobHeightToFirstDeskItem = () => {
  if (!mobViewRef.value || !firstDeskItemRef.value) return

  const firstDeskEl = firstDeskItemRef.value
  if (typeof firstDeskEl.getBoundingClientRect !== 'function') return

  // Only apply on desktop.
  if (window.matchMedia('(min-width: 1024px)').matches) {
    const h = firstDeskEl.getBoundingClientRect().height
    if (!h) return
    const heightPx = `${Math.round(h)}px`
    mobViewRef.value.style.setProperty('--desk-first-item-height', heightPx)
    // Set explicit height to avoid var()/scoping quirks in computed styles.
    mobViewRef.value.style.height = heightPx
  } else {
    mobViewRef.value.style.removeProperty('--desk-first-item-height')
    mobViewRef.value.style.removeProperty('height')
  }
}

const syncDesktopMobItemHeights = () => {
  if (!window.matchMedia('(min-width: 1024px)').matches) {
    desktopMobItemHeights.value = []
    return
  }

  desktopMobItemHeights.value = mobItemsRefs.value
    .map((el) => (el && typeof el.getBoundingClientRect === 'function')
      ? Math.round(el.getBoundingClientRect().height)
      : 0
    )
}

const applyDesktopLeftTranslate = () => {
  if (!mobItemsContainerRef.value) return
  if (!window.matchMedia('(min-width: 1024px)').matches) {
    mobItemsContainerRef.value.style.transform = ''
    activeTransformIndex.value = -1
    return
  }

  const stickyTop = 120
  const heights = desktopMobItemHeights.value
  const positions = desktopDeskItemPositions.value

  if (!Array.isArray(heights) || heights.length === 0) return
  if (!Array.isArray(positions) || positions.length === 0) return

  // cumulative heights: prefix[i] = sum heights[0..i-1]
  const prefix = new Array(heights.length + 1).fill(0)
  for (let i = 0; i < heights.length; i += 1) {
    prefix[i + 1] = prefix[i] + (heights[i] || 0)
  }

  // Determine how far the left list should be translated up.
  // Smooth rule (per item i):
  // - step starts when rightBottom crosses:
  //   stickyTop + stickyHeight + heights[i]
  // - step ends when rightBottom crosses:
  //   stickyTop + stickyHeight
  // - within that band, translate moves from prefix[i] -> prefix[i] + heights[i]
  let translate = 0
  const stickyHeight = mobViewRef.value.getBoundingClientRect().height || 0
  if (!stickyHeight) return
  let nextActiveIndex = -1
  let lastCompletedIndex = -1

  for (let i = 0; i < heights.length; i += 1) {
    const h = heights[i] || 0
    const pos = positions[i]
    if (!h || !pos) continue

    const rangeStart = stickyTop + stickyHeight + h
    const rangeEnd = stickyTop + stickyHeight
    const bottom = pos.bottom

    if (bottom <= rangeEnd) {
      // Step fully completed.
      translate = Math.max(translate, prefix[i + 1])
      lastCompletedIndex = i
      continue
    }

    if (bottom < rangeStart) {
      // Step in progress.
      const t = (rangeStart - bottom) / (rangeStart - rangeEnd) // 0..1
      const stepTranslate = prefix[i] + Math.max(0, Math.min(1, t)) * h
      translate = Math.max(translate, stepTranslate)
      nextActiveIndex = i
    }
  }

  // Prefer the currently transforming (in-progress) item; fallback to last completed so the
  // highlight doesn't disappear between steps. Recomputes each scroll, so reverse scroll works.
  activeTransformIndex.value = nextActiveIndex !== -1 ? nextActiveIndex : lastCompletedIndex
  mobItemsContainerRef.value.style.transform = `translate3d(0, ${-Math.round(translate)}px, 0)`
}

const syncDesktopDeskItemPositions = () => {
  if (!window.matchMedia('(min-width: 1024px)').matches) {
    desktopDeskItemPositions.value = []
    applyDesktopLeftTranslate()
    return
  }

  const viewportHeight = window.innerHeight || 0

  desktopDeskItemPositions.value = deskItemsRefs.value.map((el) => {
    if (!el || typeof el.getBoundingClientRect !== 'function') return null
    const rect = el.getBoundingClientRect()
    return {
      top: rect.top,
      bottom: rect.bottom,
      height: rect.height,
      isInView: rect.bottom > 0 && rect.top < viewportHeight
    }
  })

  applyDesktopLeftTranslate()
}

onMounted(async () => {
  await nextTick()
  syncMobHeightToFirstDeskItem()
  syncDesktopMobItemHeights()
  syncDesktopDeskItemPositions()

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      syncMobHeightToFirstDeskItem()
      syncDesktopMobItemHeights()
      syncDesktopDeskItemPositions()
    })
    if (firstDeskItemRef.value) resizeObserver.observe(firstDeskItemRef.value)
    mobItemsRefs.value.forEach((el) => {
      if (el) resizeObserver.observe(el)
    })
    deskItemsRefs.value.forEach((el) => {
      if (el) resizeObserver.observe(el)
    })
  }

  window.addEventListener('resize', syncMobHeightToFirstDeskItem)
  window.addEventListener('resize', syncDesktopMobItemHeights)
  window.addEventListener('resize', syncDesktopDeskItemPositions)
  window.addEventListener('scroll', syncDesktopDeskItemPositions, { passive: true })
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncMobHeightToFirstDeskItem)
  window.removeEventListener('resize', syncDesktopMobItemHeights)
  window.removeEventListener('resize', syncDesktopDeskItemPositions)
  window.removeEventListener('scroll', syncDesktopDeskItemPositions)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="scroll-showcase">
  <div class="container">
    <div class="scroll-showcase__wrapper">

      <div ref="mobViewRef" class="scroll-showcase__mob-view">
        <div class="scroll-showcase__head"
          :class="{ '--hidden-description': shouldHideHeadElements }"
          v-html="props.block?.['heading']?.html"></div>

        <div v-if="props.block?.['headingTextTable']?.length"
          class="scroll-showcase__head-table"
          :class="{ '--hidden-table': shouldHideHeadElements }">
          <div class="scroll-showcase__head-table-item"
            v-for="(item, idx) in props.block?.['headingTextTable']"
            :key="item.id">
            <span class="scroll-showcase__head-table-item-title">{{ item.heading }}</span>
            <span class="scroll-showcase__head-table-item-text">{{ item.text }}</span>
          </div>
        </div>

        <div ref="mobItemsContainerRef" class="scroll-showcase__items">
          <div class="scroll-showcase__item"
            v-for="(item, idx) in props.block?.['items']"
            :key="item.id"
            :ref="setMobItemRef(idx)"
            :class="{
              'is-active': idx === activeTransformIndex,
              '--hidden-element-tablet': activeTransformIndex >= 0 && activeTransformIndex - idx > 1,
              '--hidden-element-desktop': activeTransformIndex >= 0 && activeTransformIndex - idx > 2,
            }"
            :data-item-index="idx"
            :data-item-height="desktopMobItemHeights[idx] ?? null">
            <div class="scroll-showcase__item-title">{{ item.title }}</div>
            <div class="scroll-showcase__item-description">{{ item.description }}</div>
            <div class="scroll-showcase__item-image">
              <GlobalsImage
                :landscape="item.assetDesktop"
                :portrait="item.assetMobile"
                :title="item.title"
                :max-width="866"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="scroll-showcase__desk-view-items">
        <div class="scroll-showcase__desk-view-item"
          v-for="(item, idx) in props.block?.['items']"
          :key="item.id"
          :ref="(el) => { if (idx === 0) setFirstDeskItemRef(el); setDeskItemRef(idx)(el) }"
          :data-desk-index="idx"
          :data-desk-top="desktopDeskItemPositions[idx]?.top ?? null"
          :data-desk-in-view="desktopDeskItemPositions[idx]?.isInView ?? null">
          <div class="scroll-showcase__desk-view-item-image">
            <GlobalsImage
              :landscape="item.assetDesktop"
              :portrait="item.assetMobile"
              :title="item.title"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.scroll-showcase {
  margin-bottom: 80px;

  @media (min-width: 1024px) {
    margin-bottom: 140px;
  }
}

.scroll-showcase__wrapper {
  @media (min-width: 1024px) {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.scroll-showcase__mob-view {
  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    width: calc(573 / 1552 * 100%);
    max-width: 572px;
    position: sticky;
    top: 120px;
    flex: 1;
  }
}

.scroll-showcase__head :deep(*) {
  font-size: 28px;
  line-height: 132%; /* 36.96px */
  letter-spacing: -0.56px;
  color: var(--color-gray-800);

  @media (min-width: 1024px) {
    font-size: 38px;
    line-height: 120%; /* 45.6px */
    letter-spacing: -0.76px;
  }
}

.scroll-showcase__head :deep(p),
.scroll-showcase__head :deep(p *) {
  font-size: 16px;
  font-weight: 400;
  line-height: 146%; /* 23.36px */
  letter-spacing: -0.32px;
  color: var(--color-gray-400);
  margin-top: 16px;

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 138%; /* 24.84px */
    letter-spacing: -0.36px;
    color: var(--color-gray-500);
    margin-top: 20px;
    transition: opacity 0.5s ease, transform 0.5s ease;
  }
}

.scroll-showcase__head.--hidden-description :deep(p) {
  @media (min-width: 1024px) {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-20px);
  }
}

.scroll-showcase__items {
  margin-top: 48px;
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    will-change: transform;
    margin-top: 0;
  }
}

.scroll-showcase__item {
  border-top: 1px solid var(--color-gray-150);
  padding-top: 16px;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    padding-top: 28px;
    padding-bottom: 28px;
    opacity: 0.3;
    transition: opacity 0.15s ease;

    &.is-active {
      opacity: 1;
    }
  }
}

@media (max-width: 1439px) {
  .scroll-showcase__item.--hidden-element-tablet {
    opacity: 0;
    pointer-events: none;
  }
}

.scroll-showcase__item.--hidden-element-desktop {
  opacity: 0;
  pointer-events: none;
}

.scroll-showcase__item-title {
  font-size: 20px;
  font-weight: 500;
  line-height: 120%; /* 24px */
  letter-spacing: -0.4px;
  color: var(--color-gray-700);

  @media (min-width: 1024px) {
    line-height: 136%; /* 27.2px */
    letter-spacing: -0.4px;
    color: var(--color-gray-800);
  }
}

.scroll-showcase__item-description {
  font-size: 16px;
  line-height: 146%; /* 23.36px */
  letter-spacing: -0.32px;
  color: var(--color-gray-400);
  margin-top: 16px;

  @media (min-width: 1024px) {
    font-size: 18px;
    line-height: 140%; /* 25.2px */
    letter-spacing: -0.36px;
    margin-top: 20px;
  }
}

.scroll-showcase__item-image {
  margin-top: 24px;
  border-radius: 6.356px;
  overflow: hidden;

  @media (min-width: 1024px) {
    display: none;
  }
}

.scroll-showcase__desk-view-items {
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    flex-direction: column;
    gap: 120px;
    width: calc(867 / 1552 * 100%);
    max-width: 866px;
  }
}

.scroll-showcase__head-table {
  display: flex;
  flex-wrap: wrap;
  gap: 24px 16px;
  padding: 20px 24px;
  border-radius: 16px;
  border: 1px solid var(--color-gray-150);
  margin-top: 24px;
  background: #fff;
  position: relative;
  z-index: 1;
  transition: opacity 0.5s ease, transform 0.5s ease;

  &.--hidden-table {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-20px);
  }

  @media (min-width: 1024px) {
    flex-wrap: wrap;
    max-width: 572px;
  }

  @media (min-width: 1440px) {
    margin-top: 48px;
    padding: 20px 32px;
    gap: 24px;
  }
}

.scroll-showcase__head-table-item {
  width: calc(50% - 8px);
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (min-width: 1024px) {
    width: calc(25% - 24px / 4 * 3);
  }
}

.scroll-showcase__head-table-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.scroll-showcase__head-table-item-title {
  font-family: var(--font-family-heading);
  font-size: 38px;
  font-weight: 500;
  line-height: 120%; /* 45.6px */
  letter-spacing: -0.76px;
  color: var(--color-gray-900);
}

.scroll-showcase__head-table-item-text {
  font-size: 13px;
  line-height: 138%; /* 17.94px */
  letter-spacing: -0.26px;
  color: var(--color-gray-400);
}

.scroll-showcase__head > :deep(*) {
  @media (min-width: 1024px) {
    background: white;
    position: relative;
    z-index: 1;
  }
}

</style>
